import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  CheckCircle,
  Cancel,
  TrendingUp,
  Pending,
  Error,
  AccountBalanceWallet,
  Payment,
  ExpandMore,
  Sms,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchDeposits,
  failDeposit,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/depositsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Deposit, BankType } from '../types';
import CompleteDepositModal from '../components/deposits/CompleteDepositModal';
import FailDepositModal from '../components/deposits/FailDepositModal';

const DepositsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { deposits, pagination, statistics, loading, error } = useSelector(
    (state: RootState) => state.deposits
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [bankTypeFilter, setBankTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<Deposit | null>(null);
  const [showDepositDetails, setShowDepositDetails] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchDeposits({ page: 1, limit: 10 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) {
      // Check if search term looks like a phone number
      if (searchTerm.startsWith('251')) {
        filters.phoneNumber = searchTerm;
      } else {
        filters.search = searchTerm;
      }
    }
    if (statusFilter) filters.status = statusFilter;
    if (bankTypeFilter) filters.bankType = bankTypeFilter;
    
    dispatch(setFilters(filters));
    dispatch(fetchDeposits({ page: 1, limit: 10, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchDeposits({ page: newPage + 1, limit: 10 }) as any);
  };

  const handleViewDeposit = (deposit: Deposit) => {
    setSelectedDeposit(deposit);
    setShowDepositDetails(true);
  };

  const handleCompleteDeposit = (deposit: Deposit) => {
    setSelectedDeposit(deposit);
    setShowCompleteModal(true);
  };

  const handleFailDeposit = (deposit: Deposit) => {
    setSelectedDeposit(deposit);
    setShowFailModal(true);
  };

  const confirmFailDeposit = async (reason: string) => {
    if (!selectedDeposit) return;
    const depositId = selectedDeposit._id;
    setActionLoading(depositId);
    try {
      const result = await dispatch(failDeposit({ depositId, reason }) as any);
      if (failDeposit.fulfilled.match(result) && result.payload?.success) {
        toast.success('Deposit marked as failed');
        setShowFailModal(false);
        setSelectedDeposit(null);
        dispatch(fetchDeposits({ page: pagination.page, limit: 10 }) as any);
      } else {
        toast.error(String(result.payload || result.error?.message || 'Failed to mark deposit as failed'));
      }
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred while failing deposit');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchDeposits({ page: 1, limit: 10 }) as any);
    setSearchTerm('');
    setStatusFilter('');
    setBankTypeFilter('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const truncateText = (text: string | undefined, maxLength: number = 50) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'failed': return <Error />;
      default: return <AccountBalanceWallet />;
    }
  };

  const depositStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ];

  const bankTypes = [
    { value: BankType.TELEBIRR, label: 'Telebirr' },
    { value: BankType.AMOLE, label: 'Amole' },
    { value: BankType.CBE, label: 'CBE' },
    { value: BankType.CBEBIRR, label: 'CBE Birr' },
    { value: BankType.ABYSSINIA, label: 'Abyssinia' },
    { value: BankType.AWASH, label: 'Awash' },
    { value: BankType.OTHER, label: 'Other' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Deposits Management
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.totalCount || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Deposits
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.completed?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Pending sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.pending?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Error sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.statusBreakdown?.failed?.count || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Payment sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.averageAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Average Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Search by reference, phone, agent, or bank transaction..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {depositStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Bank Type</InputLabel>
              <Select
                value={bankTypeFilter}
                onChange={(e) => setBankTypeFilter(e.target.value)}
                label="Bank Type"
              >
                <MenuItem value="">All Banks</MenuItem>
                {bankTypes.map((bank) => (
                  <MenuItem key={bank.value} value={bank.value}>
                    {bank.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                More Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Deposits Table */}
      <Paper>
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table sx={{ minWidth: { xs: 700, md: 850 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Bank Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, maxWidth: 200 }}>SMS Text</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deposits.map((deposit) => (
                <TableRow key={deposit.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {deposit.reference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {deposit.user?.username || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {deposit.phoneNumber}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" color="success.main">
                      {formatCurrency(deposit.amount)}
                    </Typography>
                    {deposit.cryptoAmount && (
                      <Typography variant="caption" color="warning.main" fontWeight="bold" display="block">
                        🪙 {deposit.cryptoAmount} {deposit.cryptoCurrency || 'USDT'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {deposit.agentPhone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        deposit.paymentMethodType === 'crypto' || deposit.cryptoAmount || deposit.bankType === 'crypto'
                          ? 'Binance Pay'
                          : deposit.bankType
                      }
                      size="small"
                      color={
                        deposit.paymentMethodType === 'crypto' || deposit.cryptoAmount || deposit.bankType === 'crypto'
                          ? 'warning'
                          : 'primary'
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, maxWidth: 200 }}>
                    {deposit.smsText ? (
                      <Tooltip 
                        title={deposit.smsText} 
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 400,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.7,
                            },
                          }}
                        >
                          <Sms fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary',
                            }}
                          >
                            {truncateText(deposit.smsText, 40)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(deposit.status)}
                      label={deposit.status}
                      color={getStatusColor(deposit.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dayjs(deposit.timestamp).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDeposit(deposit)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {deposit.status === 'pending' && (
                        <>
                          <Tooltip title="Complete Deposit">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleCompleteDeposit(deposit)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Fail Deposit">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleFailDeposit(deposit)}
                              disabled={actionLoading === deposit._id}
                            >
                              {actionLoading === deposit._id ? <CircularProgress size={16} /> : <Cancel />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Deposit Details Modal */}
      <Dialog open={showDepositDetails} onClose={() => setShowDepositDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Deposit Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDeposit && (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Reference</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedDeposit.reference}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(selectedDeposit.status)}
                        label={selectedDeposit.status}
                        color={getStatusColor(selectedDeposit.status) as any}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {formatCurrency(selectedDeposit.amount)}
                      </Typography>
                      {selectedDeposit.cryptoAmount && (
                        <Typography variant="caption" color="warning.main" fontWeight="bold">
                          {selectedDeposit.cryptoAmount} {selectedDeposit.cryptoCurrency || 'USDT'} (Rate: 1 USDT = {selectedDeposit.exchangeRate} ETB)
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                      <Chip
                        label={
                          selectedDeposit.paymentMethodType === 'crypto' || selectedDeposit.cryptoAmount
                            ? 'Binance Pay'
                            : selectedDeposit.bankType
                        }
                        size="small"
                        color={
                          selectedDeposit.paymentMethodType === 'crypto' || selectedDeposit.cryptoAmount
                            ? 'warning'
                            : 'primary'
                        }
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">User Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                      <Typography variant="body1">{selectedDeposit.user?.username || 'Unknown User'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                      <Typography variant="body1">{selectedDeposit.phoneNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Telegram Username</Typography>
                      <Typography variant="body1">{selectedDeposit.user?.telegramUsername || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Transaction Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Agent Phone / Account</Typography>
                      <Typography variant="body1">{selectedDeposit.agentPhone}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        {selectedDeposit.cryptoAmount ? 'Binance TxID / Proof' : 'Bank Transaction ID'}
                      </Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {selectedDeposit.cryptoTxId || selectedDeposit.bankTransaction || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        SMS Text
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: 'background.default',
                          maxHeight: 200,
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            color: selectedDeposit.smsText ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {selectedDeposit.smsText || 'No SMS text available'}
                        </Typography>
                      </Paper>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                      <Typography variant="body1">{selectedDeposit.notes || 'No notes'}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Timestamps</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedDeposit.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedDeposit.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Updated At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedDeposit.updatedAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDepositDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Complete Deposit Modal */}
      <CompleteDepositModal
        open={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        deposit={selectedDeposit}
        onSuccess={() => {
          dispatch(fetchDeposits({ page: pagination.page, limit: 10 }) as any);
        }}
      />

      <FailDepositModal
        open={showFailModal}
        deposit={selectedDeposit}
        loading={Boolean(selectedDeposit && actionLoading === selectedDeposit._id)}
        onClose={() => {
          if (actionLoading) return;
          setShowFailModal(false);
        }}
        onConfirm={confirmFailDeposit}
      />
    </Box>
  );
};

export default DepositsPage; 